import { useState, useEffect } from "react";
import {
  Box,
  Button,
  VStack,
  Heading,
  Card,
  Text,
  HStack,
  SimpleGrid,
} from "@chakra-ui/react";
import { FiDatabase } from "react-icons/fi";
import api from "../lib/api";
import { toaster } from "../components/ui/toaster";

interface TransactionStats {
  total_transactions: number;
  total_revenue: number;
  total_cost: number;
  unique_products: number;
  unique_locations: number;
}

export default function DataSummaryPage() {
  const [stats, setStats] = useState<TransactionStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const response = await api.get("/admin/transactions/stats");
      setStats(response.data.data);
    } catch (error: any) {
      console.error("Stats error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "Could not fetch transaction statistics";
      toaster.create({
        title: "Failed to load stats",
        description: errorMessage,
        type: "error",
      });
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const formatCurrency = (amount: number) => {
    // Amount is in Kobo, convert to Naira for display
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(amount / 100);
  };

  return (
    <Box p={6}>
      <Heading size="xl" mb={6}>
        Data Summary
      </Heading>

      <VStack align="stretch" gap={6}>
        <Card.Root>
          <Card.Header>
            <HStack justifyContent="space-between" wrap={"wrap"}>
              <HStack gap={2}>
                <FiDatabase size={20} />
                <Heading size="lg">Transaction Statistics</Heading>
              </HStack>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={fetchStats}
                loading={loadingStats}>
                <FiDatabase style={{ marginRight: "8px" }} />
                Refresh Stats
              </Button>
            </HStack>
          </Card.Header>
          <Card.Body>
            {stats ? (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
                <Box p={4} bg="blue.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Total Transactions
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.700">
                    {stats.total_transactions.toLocaleString()}
                  </Text>
                </Box>

                <Box p={4} bg="green.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Total Revenue
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="green.700">
                    {formatCurrency(stats.total_revenue)}
                  </Text>
                </Box>


                <Box p={4} bg="orange.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Total Cost
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="orange.700">
                    {formatCurrency(stats.total_cost)}
                  </Text>
                </Box>

                <Box p={4} bg="cyan.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Unique Products
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="cyan.700">
                    {stats.unique_products.toLocaleString()}
                  </Text>
                </Box>

                <Box p={4} bg="pink.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Locations
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="pink.700">
                    {stats.unique_locations.toLocaleString()}
                  </Text>
                </Box>
              </SimpleGrid>
            ) : (
              <Text color="gray.500" textAlign="center" py={8}>
                {loadingStats
                  ? "Loading statistics..."
                  : "No statistics available."}
              </Text>
            )}
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  );
}
